import { inject, Injectable } from "@angular/core";
import { Actions, createEffect, ofType } from "@ngrx/effects";
import { of } from "rxjs";
import { catchError, mergeMap, switchMap, tap, withLatestFrom } from "rxjs/operators";
import { ContactActions } from "./contact.actions";
import { ContactsActions } from "./contacts/contacts.actions";
import { CustomerActions } from "../customer/customer.actions";
import { CustomersActions } from "../customer/customers/customers.actions";
import { Store } from "@ngrx/store";
import { selectSelectedCompany } from "../../user/user.selectors";
import { AttioClient, ContactClient } from "src/api-service";
import { Router } from "@angular/router";
import { normalizeError } from "../../app-store-error.types";

@Injectable()
export class ContactEffects {
    private readonly actions$ = inject(Actions);
    private readonly store = inject(Store);
    private readonly contactClient = inject(ContactClient);
    private readonly attioClient = inject(AttioClient);
    private readonly router = inject(Router);

    // Initialize Contact
    initializeContact$ = createEffect(() =>
        this.actions$.pipe(
            ofType(ContactActions.initializeContact),
            switchMap(({ contactId }) =>
                this.contactClient.get(contactId).pipe(
                    mergeMap(contact => [ContactActions.initializeContactSuccess({ contact })]),
                    catchError(error => {
                        console.error("Failed to fetch contact:", error);
                        return of(ContactActions.initializeContactFailure({
                            error: normalizeError(error),
                            toastKey: "CONTACT.TOAST.FETCH_ERROR",
                        }));
                    }),
                ),
            ),
        ),
    );

    // Update Contact
    updateContact$ = createEffect(() =>
        this.actions$.pipe(
            ofType(ContactActions.updateContact),
            switchMap(({ contactId, request }) =>
                this.contactClient.update(contactId, request).pipe(
                    mergeMap(contact => [
                        ContactActions.updateContactSuccess({
                            contact,
                            toastKey: "CONTACT.TOAST.UPDATE_SUCCESS",
                        }),
                    ]),
                    catchError(error => of(ContactActions.updateContactFailure({
                        error: normalizeError(error),
                        toastKey: "CONTACT.TOAST.UPDATE_ERROR",
                    }))),
                ),
            ),
        ),
    );

    // Remove Contact
    removeContact$ = createEffect(() =>
        this.actions$.pipe(
            ofType(ContactActions.removeContact),
            switchMap(({ contactId }) =>
                this.contactClient.delete(contactId).pipe(
                    mergeMap(() => [
                        ContactActions.removeContactSuccess({ toastKey: "CONTACT.TOAST.REMOVE_SUCCESS" }),
                        ContactsActions.invalidate(),
                    ]),
                    catchError(error => of(ContactActions.removeContactFailure({
                        error: normalizeError(error),
                        toastKey: "CONTACT.TOAST.REMOVE_ERROR",
                    }))),
                ),
            ),
        ),
    );

    // Create Contact
    createContact$ = createEffect(() =>
        this.actions$.pipe(
            ofType(ContactActions.createContact),
            switchMap(({ request }) =>
                this.contactClient.create(request).pipe(
                    mergeMap(contact => [
                        ContactActions.createContactSuccess({
                            contact,
                            toastKey: "CONTACT.TOAST.CREATE_SUCCESS",
                        }),
                        ContactsActions.invalidate(),
                    ]),
                    catchError(error => of(ContactActions.createContactFailure({
                        error: normalizeError(error),
                        toastKey: "CONTACT.TOAST.CREATE_ERROR",
                    }))),
                ),
            ),
        ),
    );

    // Add to customer
    addToCustomer$ = createEffect(() =>
        this.actions$.pipe(
            ofType(ContactActions.addToCustomer),
            switchMap(({ contactId, customerCode }) =>
                this.contactClient.addToCustomer(contactId, { customerCode }).pipe(
                    mergeMap(contact => [
                        ContactActions.addToCustomerSuccess({
                            contact,
                            toastKey: "CONTACT.TOAST.ADD_TO_CUSTOMER_SUCCESS",
                        }),
                        // Refresh customer to update their contacts list
                        CustomerActions.initializeCustomer({ customerCode }),
                        // Invalidate contacts list so it refreshes
                        ContactsActions.invalidate(),
                    ]),
                    catchError(error => of(ContactActions.addToCustomerFailure({
                        error: normalizeError(error),
                        toastKey: "CONTACT.TOAST.ADD_TO_CUSTOMER_ERROR",
                    }))),
                ),
            ),
        ),
    );

    // Remove from customer
    removeFromCustomer$ = createEffect(() =>
        this.actions$.pipe(
            ofType(ContactActions.removeFromCustomer),
            switchMap(({ contactId, customerCode }) =>
                this.contactClient.removeFromCustomer(contactId, customerCode).pipe(
                    mergeMap(contact => [
                        ContactActions.removeFromCustomerSuccess({
                            contact,
                            toastKey: "CONTACT.TOAST.REMOVE_FROM_CUSTOMER_SUCCESS",
                        }),
                        // Refresh customer to update their contacts list
                        CustomerActions.initializeCustomer({ customerCode }),
                        // Invalidate contacts list so it refreshes
                        ContactsActions.invalidate(),
                    ]),
                    catchError(error => of(ContactActions.removeFromCustomerFailure({
                        error: normalizeError(error),
                        toastKey: "CONTACT.TOAST.REMOVE_FROM_CUSTOMER_ERROR",
                    }))),
                ),
            ),
        ),
    );

    // ************************************************** NAVIGATION EFFECTS **************************************************
    // Navigate to contact page after create
    navigateToContact$ = createEffect(
        () =>
            this.actions$.pipe(
                ofType(ContactActions.createContactSuccess),
                withLatestFrom(this.store.select(selectSelectedCompany)),
                tap(([{ contact }, selectedCompany]) => {
                    this.router.navigate([selectedCompany.id, "contact", contact.id]);
                }),
            ),
        { dispatch: false },
    );

    // Navigate to list after remove
    navigateToListAfterRemove$ = createEffect(
        () =>
            this.actions$.pipe(
                ofType(ContactActions.removeContactSuccess),
                withLatestFrom(this.store.select(selectSelectedCompany)),
                tap(([, selectedCompany]) => {
                    this.router.navigate([selectedCompany.id, "contact"]);
                }),
            ),
        { dispatch: false },
    );

    // ************************************************** ATTIO SYNC EFFECTS **************************************************
    syncFromAttio$ = createEffect(() =>
        this.actions$.pipe(
            ofType(ContactActions.syncFromAttio),
            switchMap(({ contactId, attioRecordId }) =>
                this.attioClient.syncContact(attioRecordId).pipe(
                    mergeMap(() => [
                        ContactActions.syncFromAttioSuccess({ contactId, toastKey: "CONTACT.TOAST.SYNC_SUCCESS" }),
                        ContactActions.initializeContact({ contactId }),
                        ContactsActions.invalidate(),
                        CustomersActions.invalidate(),
                    ]),
                    catchError(error => of(ContactActions.syncFromAttioFailure({
                        error: normalizeError(error),
                        toastKey: "CONTACT.TOAST.SYNC_ERROR",
                    }))),
                ),
            ),
        ),
    );
}
